import React from "react"
import axios from "axios"

export class ApplicationFormLogic extends React.Component {
  constructor(props) {
    super(props)
    this.fileBuffer = []  // Buffer to store uploaded files so we can batch-update the state
    // List of form field names in this form. Use these fields to update the
    // remote state when saving the application.
    this.state = {
      // Status of the application, for controlling the info modal.
      status: 'normal',
      // Whether the application is in the process of being saved as a draft. Used to
      // disable and animate the save button during draft saving.
      saving: false,
      // Whether the application is in the process of being submitted. Used to
      // disable and animate the submit button during application submission.
      submitting: false,
      // Whether the application has already been submitted, according to form data
      // loaded from the drafts remote storage. Used to disable form editing/submission.
      submitted: false,
      // What submission step the application is on, to expose the steps to the end user.
      submitStep: 1,
      permalink: '',
      files: [],
      budget: [],  // Array holding a File object representing the project budget.
      plan: [],  // Array holding a File object representing the community plan.
      letter: [],  // Array holding a File object representing the community letter of support.
    }
  }

  getFormData = () => {
    // Read the state of this component and retrieve form data.
    let formData = {}
    for (let field of this.props.data.fields) {
      formData[field] = this.state[field]
    }

    // Add application type to form data
    formData.applicationType = this.props.data.applicationType

    // Add the URL of the page to the form data, for generating permalinks.
    // Previously we tried using the Referer header for this purpose but
    // IE11 implements it differently than other browsers so this workaround
    // should ensure that all browsers send a properly-formatted URL.
    formData.referer = window.location.href

    // Serialize file uploads to JSON.
    const serializeFiles = rawFile => {
      return {
        id: rawFile.id,
        saved: true,
        options: {
          type: 'local',
          file: {
            name: rawFile.options.file.name,
            type: rawFile.options.file.type,
            size: rawFile.options.file.size
          }
        }
      }
    }
    // Not all forms will uses these file uploads. In that case, we'll save them
    // as empty arrays in the form state. This is useful because the backend submit API
    // will need to check for these fields whether or not they exist on the form,
    // and if they don't exist, it can just skip them for final processing.
    formData.files = this.state.files.map(serializeFiles)
    formData.budget = this.state.budget.map(serializeFiles)
    formData.plan = this.state.plan.map(serializeFiles)
    formData.letter = this.state.letter.map(serializeFiles)
    return formData
  }

  handleInputChange = event => {
    const target = event.target
    const value = target.files ? target.files[0].name : target.value
    const name = target.name

    this.setState({
      [name]: value,
    })
  }

  handleProcessFile = (error, newFile) => {
    // Handle the user changing the FilePond file selection.
    const file = {
      id: newFile.serverId,
      options: {
        type: 'local',
        file: newFile.file,
        saved: false
      }
    }
    // Push the files to a buffer instead of saving them directly to state. This
    // is important because the files can be uploaded in parallel, so we need to
    // update them in a batch.
    this.fileBuffer.push(file)
  }

  handleProcessBudget = (error, newFile) => {
    // Handle the user changing the project budget selection.
    this.setState({budget: [{
      id: newFile.serverId,
      options: {
        type: 'local',
        file: newFile.file,
        saved: false
      }
    }]})
  }

  handleProcessPlan = (error, newFile) => {
    // Handle the user changing the project community plan selection.
    this.setState({plan: [{
      id: newFile.serverId,
      options: {
        type: 'local',
        file: newFile.file,
        saved: false
      }
    }]})
  }

  handleProcessLetter = (error, newFile) => {
    // Handle the user changing the letter of community support.
    this.setState({letter: [{
      id: newFile.serverId,
      options: {
        type: 'local',
        file: newFile.file,
        saved: false
      }
    }]})
  }

  _handleProcessFiles = (fileStateName) => {
    // When all FilePond files have been uploaded, push the buffer to state.
    const existingFiles = this.state[fileStateName]
    const newFiles = this.fileBuffer
    this.setState({[fileStateName]: existingFiles.concat(newFiles)})
    this.fileBuffer = []  // Reset the buffer
  }

  handleProcessFiles = () => {
    return this._handleProcessFiles('files')
  }

  handleProcessPlans = () => {
    return this._handleProcessFiles('plan')
  }

  _handleRemoveFile = (file, fileStateName) => {
    // Remove a file from state when the user clears it in the FilePond widget.
    let files = this.state[fileStateName]
    for (let i=0; i<files.length; i++) {
      if (files[i].id === file.serverId || (file.serverId && files[i].id === file.serverId.id)) {
        // Due to the state update, FilePond will try to remove all files
        // immediately after uploading. Prevent this from happening by assigning
        // a 'saved' attribute to files that we can use to check whether the
        // file has been saved to state or not.
        if (files[i].saved) {
          files.splice(i, 1)
        } else {
          files[i].saved = 1
        }
      }
    }
    this.setState({[fileStateName]: files})
  }

  handleRemoveFile = (error, file) => {
    return this._handleRemoveFile(file, 'files')
  }

  handleRemoveBudget = (error, file) => {
    return this._handleRemoveFile(file, 'budget')
  }

  handleRemovePlan = (error, file) => {
    return this._handleRemoveFile(file, 'plan')
  }

  handleRemoveLetter = (error, file) => {
    return this._handleRemoveFile(file, 'letter')
  }

  handleSubmit = async event => {
    event.preventDefault()

    this.setState({submitting: true, status: 'submitProgress'})
    const submitUrl = `${process.env.GATSBY_API_URL}/api/submit/${this.props.data.applicationType}/`
    const params = (this.props.uuid) ? {uuid: this.props.uuid} : {}

    const formData = this.getFormData()
    const data = {
      "formData": formData,
      "referer": formData.referer,
      "fields": this.props.data.fields
    }

    try {
      // Step 1: Create the application.
      const createAppResponse = await axios.post(
        submitUrl,
        data,
        {params: Object.assign(params, {step: 1})}
      )
      const folderId = createAppResponse.data

      // Step 2: Save images and other attachments.
      this.setState({submitStep: 2})
      await axios.post(submitUrl, data, {params: Object.assign(params, {step: 2, folderId})})

      // Step 3: Copy the application template and merge it.
      this.setState({submitStep: 3})
      await axios.post(submitUrl, data, {params: Object.assign(params, {step: 3, folderId})})

      // Step 4: Save a final draft of the application, and save applicant contact info.
      this.setState({submitStep: 4})
      const permalinkResponse = await axios.post(
        submitUrl,
        data,
        {params: Object.assign(params, {step: 4})}
      )
      const permalink = permalinkResponse.data
      this.setState({submitted: true, submitStep: 5, permalink, status: 'submitSuccess'})
    } catch(error) {
      console.log(error)
      this.setState({status: 'submitError'})
    } finally {
      this.setState({submitting: false})
    }
  }

  handleSave = event => {
    event.preventDefault()
    this.setState({saving: true})
    let formData = this.getFormData()
    let axiosConfig = {
      data: formData,
      url: `${process.env.GATSBY_API_URL}/api/drafts/`,
      method: 'post',
    }
    if (this.props.uuid) {  // Draft already exists, so update it
      axiosConfig.method = 'put'
      axiosConfig.url += `${this.props.uuid}/`
    }
    axios(axiosConfig).then(response => {
      const permalink = response.data
      this.setState({permalink, status: 'saveSuccess'})
    }).catch(error => {
      this.setState({status: 'saveError'})
    }).finally(() => {
      this.setState({saving: false})
    })
  }

  closeModal = () => {
    // The modal is controlled by the 'status' state value, so adjust that
    // value to close the modal.
    this.setState({status: 'normal'})
  }

  componentWillMount() {
    // Before the Page component renders, check if a UUID parameter has been found
    // in the URL, and if so, display a loading animation while the saved form
    // data loads.
    if (this.props.uuid !== undefined) {
      const draftUrl = `${process.env.GATSBY_API_URL}/api/drafts/${this.props.uuid}/`
      axios.get(draftUrl).then(response => {
        this.setState({...response.data})
      }).catch(error => {
        console.log(error)
        this.setState({status: 'loadingError'})
      })
    }
  }
}

export default ApplicationFormLogic
